<template>
  <div class="filter-items">
    <template v-for="(item, index) in filterLayout" :key="item.name">
      <etg-button
        v-if="item.name === FilterTypeEnum.NOW"
        :class="{ active: filterActiveState[item.name] }"
        :size="appStore.isMobileScreen ? 'small' : 'medium'"
        type="secondary"
        :icon="item.icon"
        @click="onNowFilterClick"
      >
        {{ item.label }}
      </etg-button>
      <component
        :is="filterWrapperComponent"
        v-else
        v-model="isContentShownCollector[item.name]"
        :is-clear-shown="!!item.clearable && !isEqual(baseFilterState[item.name], filterState[item.name])"
        :title="item.label"
        :trigger-icon="item.icon"
        :trigger-label="getFilterItemLabel(item)"
        :trigger-active="filterActiveState[item.name]"
        :trigger-class="props.triggerClass"
        :trigger-btn-type="props.triggerBtnType"
        :is-loading="isProductsCountFetching"
        :submit-label="filterWrapperSubmitLabel"
        :is-footer-actions-shown="showFilterWrapperFooterActions(item.name)"
        @clear="$emit('clear', item.name)"
        @submit="handleSubmit(item.name)"
        @close="$emit('close', item.name)"
      >
        <suspense>
          <component
            :is="getFilterItemComponent(item.type!)"
            v-model="filterState[item.name]"
            v-bind="filterItemOption[item.name]"
            :should-submit-on-select="!showFilterWrapperFooterActions(item.name)"
            @submit="handleSubmit(item.name)"
          />
          <template #fallback>
            <div class="w-full h-25 flex justify-center items-center md:min-w-90">
              <etg-loading class="size-10 text-red-50" />
            </div>
          </template>
        </suspense>
      </component>
      <slot v-if="index !== filterLayout.length - 1" name="divider" />
    </template>
  </div>
</template>

<script lang="ts" setup>
import type { DeepReadonly } from 'vue'
import { format } from 'date-fns'
import { isEqual, mapValues } from 'lodash-es'
import { filterAvailableTimes, getTimeFilterDisplayText } from '@myfunnow/web-core/time-filter'

import EtgLoading from '@/components/common/etg-loading.vue'

import type { EtgButtonProps } from '@/types/component'
import { FilterUiTypeEnum, FilterTypeEnum } from '@/types/filters'
import type { FilterComponentState, FilterLayout } from '@/types/filters'
import type { GetProductsReqParams } from '@/types/product'
import type { SfilterTag } from '@/types/api'
import { NOW_EXCLUSIVE_FILTER_TYPES, TIME_PERIOD_I18N_MAP } from '@/constants/filters'
import { useAppStore } from '@/store/app'

interface Emits {
  (event: 'clear', name: string): void
  (event: 'submit', name: string): void
  (event: 'close', name: string): void
}

interface Props {
  baseFilterState: DeepReadonly<FilterComponentState>
  lastSubmitFilterState: DeepReadonly<FilterComponentState>
  filterLayout: FilterLayout[]
  filterItemOption: Record<string, any>
  productCountQueryParams: GetProductsReqParams
  triggerClass?: string
  triggerBtnType?: EtgButtonProps['type']
  submitLabel?: string
  filterMaxCount?: number
  showFilterWrapperFooterActions?: (name: FilterLayout['name']) => boolean
}

const FilterDrawer = defineAsyncComponent(() => dynamicImport(() => import('@/components/search/filter/filter-drawer.vue'), 'filter-drawer'))
const FilterPopup = defineAsyncComponent(() => dynamicImport(() => import('@/components/search/filter/filter-popup.vue'), 'filter-popup'))
const FilterItemCheckbox = defineAsyncComponent(() => import('@/components/search/filter/filter-item-checkbox.vue'))
const FilterItemRadio = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-radio.vue'), 'filter-item-radio')
)
const FilterItemRadioButton = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-radio-button.vue'), 'filter-item-radio-button')
)
const FilterItemRange = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-range.vue'), 'filter-item-range')
)
const FilterItemMarkRange = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-mark-range.vue'), 'filter-item-mark-range')
)
const FilterItemDatePicker = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-date-picker.vue'), 'filter-item-date-picker')
)
const FilterItemSelect = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-select.vue'), 'filter-item-select')
)
const FilterItemTimePicker = defineAsyncComponent(() =>
  dynamicImport(() => import('@/components/search/filter/filter-item-time-picker.vue'), 'filter-item-time-picker')
)

const emit = defineEmits<Emits>()

const props = withDefaults(defineProps<Props>(), { filterMaxCount: 0, showFilterWrapperFooterActions: () => true })
const { lastSubmitFilterState, filterItemOption, productCountQueryParams } = toRefs(props)

const filterState = defineModel<FilterComponentState>('filterState', { required: true })

const { t } = useI18n()

const appStore = useAppStore()

const FILTER_ITEM_COMPONENT_MAP = {
  [FilterUiTypeEnum.CHECK_BOX]: FilterItemCheckbox,
  [FilterUiTypeEnum.RADIO]: FilterItemRadio,
  [FilterUiTypeEnum.RADIO_BUTTON]: FilterItemRadioButton,
  [FilterUiTypeEnum.RANGE]: FilterItemRange,
  [FilterUiTypeEnum.MARK_RANGE]: FilterItemMarkRange,
  [FilterUiTypeEnum.DATE_RANGE]: FilterItemDatePicker,
  [FilterUiTypeEnum.SELECT]: FilterItemSelect,
  [FilterUiTypeEnum.TIME_PICKER]: FilterItemTimePicker,
}

const { data: filterProductCount, pending: isProductsCountFetching } = useRestaurantCount(productCountQueryParams)

// UI
const isContentShownCollector = ref(props.filterLayout.reduce<Record<string, boolean>>((acc, item) => ({ ...acc, [item.name]: false }), {}))

const productCount = computed(() => filterProductCount.value || 0)
const filterWrapperComponent = computed(() => (appStore.isMobileScreen ? FilterDrawer : FilterPopup))
const filterWrapperSubmitLabel = computed(() => {
  if (props.submitLabel) {
    return props.submitLabel
  }

  const i18nKey = productCount.value > props.filterMaxCount ? 'filter.match_count.exceed' : 'filter.match_count.within'
  const count = productCount.value > props.filterMaxCount ? props.filterMaxCount : productCount.value

  return t(i18nKey, { count })
})
const timePeriodLabelMap = computed(() => mapValues(TIME_PERIOD_I18N_MAP, info => t(info.labelI18n)))

const filterActiveState = computed(() =>
  Object.entries(filterState.value).reduce(
    (acc, [key, value]) => {
      const isActive = (() => {
        if (!Array.isArray(value)) {
          return !!value
        }
        if (value.length === 0) {
          return false
        }

        return key === FilterTypeEnum.PRICE ? value.some(v => typeof v === 'number') : value.some(v => !!v)
      })()

      acc[key as FilterTypeEnum] = isActive

      return acc
    },
    {} as Record<string, boolean>
  )
)

const getFilterItemComponent = (type: FilterUiTypeEnum) => FILTER_ITEM_COMPONENT_MAP[type] || 'div'

function getFilterItemLabel(item: FilterLayout) {
  switch (item.name) {
    case FilterTypeEnum.DATE: {
      if (lastSubmitFilterState.value.date === null) return item.label

      const toDate = new Date(lastSubmitFilterState.value.date)

      return format(toDate, 'MM/dd')
    }

    case FilterTypeEnum.FILTER_TIMES: {
      const selectedTimes = lastSubmitFilterState.value[FilterTypeEnum.FILTER_TIMES]
      const selectedDate = lastSubmitFilterState.value[FilterTypeEnum.DATE]

      return getTimeFilterDisplayText({
        selectedTimes,
        selectedDate,
        periodLabelMap: timePeriodLabelMap.value,
        defaultLabel: item.label ?? '',
        formatExcessLabel: count => t('filter.time.display', { count }),
      })
    }

    case FilterTypeEnum.PRICE: {
      if (lastSubmitFilterState.value.price[0] === null && lastSubmitFilterState.value.price[1] === null) return item.label

      const values = lastSubmitFilterState.value.price
      const formatter = filterItemOption.value[FilterTypeEnum.PRICE].valueFormatter

      return formatter(values)?.label
    }

    case FilterTypeEnum.LIMIT: {
      const limit = lastSubmitFilterState.value[FilterTypeEnum.LIMIT]

      return limit === 0 ? item.label : t('filter.people.selected', { pax: limit }, limit)
    }

    case FilterTypeEnum.DISCOUNT: {
      const state = lastSubmitFilterState.value[item.name]
      const tags = filterItemOption.value[item.name].options.tags as { id: number; name: string }[]

      return state === 0 ? item.label : tags.find(tag => tag.id === state)?.name || item.label
    }

    case FilterTypeEnum.RATING: {
      const state = lastSubmitFilterState.value[item.name]

      return state === 0 ? item.label : `${state}+`
    }

    default: {
      const state = lastSubmitFilterState.value[item.name] as number[]
      const optionTags = filterItemOption.value[item.name]?.options?.tags || []
      if (!item.name || !state || state.length === 0) return item.label || ''
      if (state.length > 2) return `${item.label}(${state.length})`

      return state.map(f => optionTags.find((t: SfilterTag) => f === t.id)?.name).join(', ')
    }
  }
}

function handleSubmit(name: string) {
  isContentShownCollector.value[name] = false

  const isNowFilterSelected = filterState.value[FilterTypeEnum.NOW] === 1

  // 與 NOW 互斥的 filter 被提交時，清除 NOW 即可
  // 清除 NOW 本身會觸發 isNonSubmitButtonChanged，進而自動觸發搜尋，不需要再 emit submit
  if (NOW_EXCLUSIVE_FILTER_TYPES.has(name) && isNowFilterSelected) {
    emit('clear', FilterTypeEnum.NOW)
    return
  }

  // 日期變更時，移除已不可選的時間
  if (name === FilterTypeEnum.DATE) {
    cleanupUnavailableFilterTimes()
  }

  emit('submit', name)
}

function cleanupUnavailableFilterTimes() {
  const selectedTimes = filterState.value[FilterTypeEnum.FILTER_TIMES]

  if (selectedTimes.length === 0) {
    return
  }

  const availableTimes = filterAvailableTimes(selectedTimes, filterState.value[FilterTypeEnum.DATE])

  if (availableTimes.length !== selectedTimes.length) {
    filterState.value[FilterTypeEnum.FILTER_TIMES] = availableTimes
  }
}

function onNowFilterClick() {
  const isFilterDateSelected = filterState.value[FilterTypeEnum.DATE] !== null
  const isFilterTimesSelected = filterState.value[FilterTypeEnum.FILTER_TIMES].length > 0

  filterState.value[FilterTypeEnum.NOW] = filterState.value[FilterTypeEnum.NOW] === 1 ? 0 : 1

  if (isFilterDateSelected) {
    emit('clear', FilterTypeEnum.DATE)
  }
  if (isFilterTimesSelected) {
    emit('clear', FilterTypeEnum.FILTER_TIMES)
  }
}
</script>
